/**
 * Schedule Gantt — center-stage default tab.
 *
 * 24-hour horizontal timeline (today, local time). Each row = one
 * launchagent. Fires render as colored blocks; "now" is a green
 * vertical line that ticks across as the day progresses.
 *
 * Source: store.schedule (which mirrors mac-status.json launchagents).
 *
 * Two density modes:
 *   • "high-signal" (default) — scrape/picker/grader/probables/sync only
 *   • "all"                   — every loaded agent
 *
 * Click any fire block → modal with the agent's metadata + log path.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { ScheduleEntry } from "../data/types";

// Visible window: today, host-local TZ, 00:00 → 23:59
function _windowBoundsLocal(): { start: number; end: number } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
  const end = start + 24 * 60 * 60_000;
  return { start, end };
}

const HIGH_SIGNAL_CATEGORIES = new Set(["scrape", "picker", "probables", "grader", "sync"]);

const CATEGORY_COLOR: Record<string, string> = {
  scrape:      "var(--accent-blue)",
  picker:      "var(--accent-purple)",
  probables:   "var(--accent-orange)",
  grader:      "var(--accent-green)",
  sync:        "var(--text-tertiary)",
  data:        "var(--accent-blue-light)",
  calibration: "var(--accent-amber)",
  telemetry:   "var(--accent-purple-light)",
  infra:       "var(--text-quaternary)",
  other:       "var(--text-quaternary)",
};

interface ViewState {
  density: "high-signal" | "all";
  selectedAgent: string | null;
}
const view: ViewState = { density: "high-signal", selectedAgent: null };

export function renderScheduleGantt(onChange: () => void): TemplateResult {
  // Cast: schedule endpoint returns enriched entries with calendar_fires +
  // start_interval_seconds + last_fire + next_fire (see worker handleSchedule).
  type Enriched = ScheduleEntry & {
    loaded?: boolean;
    last_fire?: string | null;
    next_fire?: string | null;
    last_exit_code?: number | null;
    log_path?: string | null;
    start_interval_seconds?: number | null;
    calendar_fires?: { hour: number | null; minute: number; weekday: number | null }[];
  };
  const e = store.get<Enriched[]>("schedule");
  if (e.error) return ganttError(e.error);
  if (!e.data && e.loading) return ganttSkeleton();
  if (!e.data || e.data.length === 0) return ganttEmpty();

  // Hide unloaded agents — they're plist files on disk that aren't
  // active in launchctl (typically post-migration leftovers). Showing
  // them as grey rows just clutters the Gantt; they'll surface in the
  // Anomalies panel if loaded=false should actually concern us.
  let agents = e.data.filter((a) => a.loaded !== false);
  if (view.density === "high-signal") {
    agents = agents.filter((a) => HIGH_SIGNAL_CATEGORIES.has(a.category as string));
  }
  // Sort: failed first, then by category, then alphabetically
  agents = [...agents].sort((a, b) => {
    const aFail = (a.last_exit_code ?? 0) !== 0 ? 0 : 1;
    const bFail = (b.last_exit_code ?? 0) !== 0 ? 0 : 1;
    if (aFail !== bFail) return aFail - bFail;
    const cat = (a.category ?? "z").localeCompare(b.category ?? "z");
    if (cat !== 0) return cat;
    return a.agent_label.localeCompare(b.agent_label);
  });

  const { start, end } = _windowBoundsLocal();
  const now = Date.now();
  const nowPct = ((now - start) / (end - start)) * 100;

  return html`
    <div class="panel" style="padding-bottom:10px;">
      ${ganttHeader(onChange, agents.length, e.data.length)}
      <div style="margin-top:10px;">
        ${timeAxis(nowPct)}
        ${agents.map((a) => ganttRow(a, start, end, now, () => {
          view.selectedAgent = a.agent_label;
          onChange();
        }))}
      </div>
    </div>
    ${view.selectedAgent ? agentDetailPanel(
      e.data.find((a) => a.agent_label === view.selectedAgent),
      () => { view.selectedAgent = null; onChange(); },
    ) : ""}
  `;
}

function ganttHeader(onChange: () => void, shown: number, total: number): TemplateResult {
  return html`
    <div class="panel__title" style="margin-bottom:0;">
      <span>Schedule · 24h</span>
      <span style="display:flex;align-items:center;gap:10px;">
        <span class="panel__title-actions">${shown}/${total} agents</span>
        <span style="display:inline-flex;background:var(--bg-glass-thin);
                      border:1px solid var(--border-1);border-radius:8px;padding:2px;">
          ${(["high-signal", "all"] as const).map((mode) => html`
            <button
              @click=${() => { view.density = mode; onChange(); }}
              style="padding:4px 10px;font-size:11px;font-weight:500;
                      border:none;cursor:pointer;border-radius:6px;
                      background:${view.density === mode ? "var(--bg-glass-thick)" : "transparent"};
                      color:${view.density === mode ? "var(--text-primary)" : "var(--text-tertiary)"};
                      font-family:inherit;
                      letter-spacing:0.04em;text-transform:uppercase;">
              ${mode === "high-signal" ? "key" : "all"}
            </button>
          `)}
        </span>
      </span>
    </div>
  `;
}

// Time axis with hour ticks + "now" line.
function timeAxis(nowPct: number): TemplateResult {
  const ticks = [0, 3, 6, 9, 12, 15, 18, 21, 24];
  return html`
    <div style="position:relative;height:24px;
                 border-bottom:1px solid var(--border-1);
                 margin-left:200px;margin-bottom:6px;">
      ${ticks.map((h) => html`
        <div style="position:absolute;left:${(h / 24) * 100}%;top:0;height:100%;
                     border-left:1px solid var(--border-1);
                     padding-left:4px;
                     font-size:10px;color:var(--text-quaternary);
                     letter-spacing:0.06em;
                     display:flex;align-items:flex-end;padding-bottom:4px;">
          ${String(h).padStart(2, "0")}:00
        </div>
      `)}
      <div style="position:absolute;left:${nowPct}%;top:-4px;bottom:-6px;
                   width:2px;background:var(--accent-green);
                   box-shadow:0 0 8px var(--accent-green);
                   pointer-events:none;"></div>
    </div>
  `;
}

interface AgentRow {
  agent_label: string;
  display_name: string;
  category: string | null;
  schedule: string;
  loaded?: boolean | undefined;
  last_fire?: string | null | undefined;
  next_fire?: string | null | undefined;
  last_exit_code?: number | null | undefined;
  start_interval_seconds?: number | null | undefined;
  calendar_fires?: { hour: number | null; minute: number; weekday: number | null }[] | undefined;
}

function ganttRow(a: AgentRow, start: number, end: number, now: number, onClick: () => void): TemplateResult {
  const fires = expandFires(a, start, end);
  const color = CATEGORY_COLOR[a.category ?? "other"] ?? "var(--text-quaternary)";
  const isInterval = !!a.start_interval_seconds;
  const dotCls =
    !a.loaded                                               ? "is-mute"
    : a.last_exit_code != null && a.last_exit_code !== 0    ? "is-bad"
    : "is-ok";

  return html`
    <div @click=${onClick}
         style="display:grid;grid-template-columns:200px 1fr;
                  align-items:center;gap:12px;padding:5px 0;
                  border-bottom:1px solid var(--border-1);
                  cursor:pointer;
                  transition:background var(--duration-fast) var(--ease-out);"
         onmouseover="this.style.background='var(--bg-glass-thin)'"
         onmouseout="this.style.background='transparent'"
         title="${a.display_name} · ${a.schedule}">
      <span style="display:flex;align-items:center;gap:7px;
                    font-size:12px;color:var(--text-secondary);
                    font-weight:500;
                    overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
        <span class="status-dot ${dotCls}" style="flex-shrink:0;"></span>
        ${a.display_name}
      </span>
      <div style="position:relative;height:16px;
                   background:var(--bg-glass-thin);border-radius:3px;">
        ${isInterval ? html`
          <div style="position:absolute;inset:3px 0;
                        background:linear-gradient(90deg, transparent 0%,
                          ${color} 5%, ${color} 95%, transparent 100%);
                        opacity:0.45;border-radius:2px;"></div>
        ` : fires.map((f) => {
          const pct = ((f - start) / (end - start)) * 100;
          const inFuture = f > now;
          const isMostRecent = !inFuture && Math.abs(f - now) < 60 * 60_000;
          return html`
            <div style="position:absolute;left:${pct}%;top:2px;bottom:2px;
                          width:6px;
                          margin-left:-3px;
                          background:${color};
                          opacity:${inFuture ? 0.4 : isMostRecent ? 1 : 0.7};
                          border-radius:2px;
                          ${isMostRecent ? `box-shadow:0 0 6px ${color};` : ""}"></div>
          `;
        })}
      </div>
    </div>
  `;
}

// Expand a single agent's schedule into discrete fire timestamps within
// [start, end]. For StartInterval-based agents we return [] and the
// renderer draws a continuous line instead.
function expandFires(a: AgentRow, start: number, end: number): number[] {
  if (a.start_interval_seconds) return [];  // rendered as line
  const fires = a.calendar_fires ?? [];
  const out: number[] = [];
  for (const f of fires) {
    if (f.hour == null) continue;
    const d = new Date(start);
    d.setHours(f.hour, f.minute ?? 0, 0, 0);
    const ts = d.getTime();
    if (ts >= start && ts < end) out.push(ts);
  }
  return out;
}

function ganttSkeleton(): TemplateResult {
  return html`
    <div class="panel">
      <div class="panel__title"><span>Schedule · 24h</span></div>
      ${Array.from({ length: 8 }, () => html`
        <div class="skeleton" style="height:16px;margin-bottom:6px;"></div>
      `)}
    </div>
  `;
}

function ganttEmpty(): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;
                   line-height:1.5;max-width:400px;margin:0 auto;">
        No schedule data yet. Make sure
        <code style="color:var(--text-secondary);">com.yurysankov.console-reporter</code>
        is loaded — it produces the data feed for this view.
      </div>
    </div>
  `;
}

function ganttError(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Schedule</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;max-width:680px;margin:8px auto 0;
                   font-family:monospace;word-break:break-all;
                   white-space:pre-wrap;">
        ${err.message}
      </div>
    </div>
  `;
}

function agentDetailPanel(a: AgentRow | undefined, onClose: () => void): TemplateResult {
  if (!a) return html``;
  return html`
    <div class="panel" style="
      position:fixed;
      right:24px;top:80px;
      width:480px;max-height:70vh;
      z-index:9999;
      box-shadow:var(--shadow-3);
      backdrop-filter:var(--glass-blur-thick);
      -webkit-backdrop-filter:var(--glass-blur-thick);
      overflow-y:auto;
    ">
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:14px;">
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;color:var(--text-tertiary);
                        font-family:var(--font-mono);
                        margin-bottom:2px;">
            ${a.agent_label}
          </div>
          <h2 style="margin:0;font-size:18px;color:var(--text-primary);
                       letter-spacing:-0.01em;">
            ${a.display_name}
          </h2>
        </div>
        <button @click=${onClose}
                style="background:var(--bg-glass-medium);
                       border:1px solid var(--border-1);color:var(--text-primary);
                       width:32px;height:32px;border-radius:8px;cursor:pointer;
                       font-size:18px;font-family:inherit;line-height:1;">×</button>
      </div>

      ${row("Category", a.category ?? "—")}
      ${row("Schedule", a.schedule)}
      ${row("Loaded", a.loaded ? "yes" : "no")}
      ${row("Last exit code",
             a.last_exit_code == null ? "—"
             : a.last_exit_code === 0 ? html`<span style="color:var(--accent-green-light);">0 (ok)</span>`
             : html`<span style="color:var(--accent-red-light);">${a.last_exit_code} (failed)</span>`)}
      ${row("Last fire", fmtRelative(a.last_fire))}
      ${row("Next fire", fmtRelative(a.next_fire))}
      ${a.start_interval_seconds ? row("Interval", `${a.start_interval_seconds}s`) : ""}
      ${a.calendar_fires && a.calendar_fires.length > 0 ? row(
          "Calendar",
          a.calendar_fires.map((f) =>
            f.hour == null ? `each :${String(f.minute).padStart(2, "0")}`
                            : `${String(f.hour).padStart(2, "0")}:${String(f.minute).padStart(2, "0")}`
          ).join(", ")
        ) : ""}

      <div style="margin-top:14px;padding-top:14px;
                   border-top:1px solid var(--border-1);
                   font-size:11px;color:var(--text-quaternary);
                   line-height:1.6;">
        Phase 2 will add inline log tail. For now copy + view in terminal:
        <div style="margin-top:8px;padding:8px;
                     background:var(--bg-base);border-radius:6px;
                     font-family:var(--font-mono);font-size:11px;
                     color:var(--text-secondary);
                     white-space:nowrap;overflow-x:auto;">
          tail -50 ${a.last_fire ? "" : "/tmp/" + a.agent_label.replace("com.yurysankov.", "") + ".log"}
        </div>
      </div>
    </div>
  `;
}

function row(label: string, value: unknown): TemplateResult {
  return html`
    <div style="display:grid;grid-template-columns:120px 1fr;gap:12px;
                  padding:6px 0;border-bottom:1px solid var(--border-1);
                  font-size:13px;">
      <span style="color:var(--text-tertiary);font-size:11px;
                    letter-spacing:0.06em;text-transform:uppercase;">
        ${label}
      </span>
      <span style="color:var(--text-secondary);
                    font-variant-numeric:tabular-nums;">
        ${value}
      </span>
    </div>
  `;
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (isNaN(ts)) return "—";
  const delta = ts - Date.now();
  const abs = Math.abs(delta);
  const past = delta < 0;
  const m = Math.round(abs / 60_000);
  if (m < 1)        return past ? "just now" : "imminent";
  if (m < 60)       return past ? `${m}m ago` : `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24)       return past ? `${h}h ago` : `in ${h}h`;
  const d = Math.round(h / 24);
  return past ? `${d}d ago` : `in ${d}d`;
}
